import { TrainTypeEnum } from "../Enums/globalDefaultEnum";
import { IBookedOffer, IDescriptiveText, ILeg, ILegTrain, IOffer } from "../Index";

export type OfferPassengerRef = { offerId: string; passengerRefs: string[] };

export type PriceCategory = {
  price: number;
  isSoldOut: boolean;
  discount?: number;
  seatsLeft?: number;
  offerId?: string;
  offerIds?: string[]; // Array of IDs for INDIVIDUAL mode aggregate offers
  basePrice?: number;
  originalPrice?: number;
  descriptionText?: string;
  productName?: string;
  descriptiveTexts?: IDescriptiveText[];
  isRecommended?: boolean;
  offerPassengerRefs?: OfferPassengerRef[];
};

export type DynamicPriceCategory = PriceCategory & {
  productCode: string;
  productName: string;
  serviceClassType: string;
  productTags?: string[];
};

export type PriceInfo = {
  // Dynamic product arrays grouped by service class (new)
  standardProducts: DynamicPriceCategory[];
  premiumProducts: DynamicPriceCategory[];

  // Backward-compat aliases (populated from dynamic arrays)
  standardValue: PriceCategory;
  standardPro: PriceCategory;
  premium: PriceCategory;

  shuttle?: PriceCategory;
  standardDuo?: PriceCategory;
  standardQuad?: PriceCategory;
  premiumDuo?: PriceCategory;
  premiumQuad?: PriceCategory;
};

export type StopType = {
  name: string;
  time: string;
  trainNumber: string;
  lastNumber?: string;
  duration?: string;
  durationInMinutes?: number;
  statusTime?: string;
  statusType: "normal" | "stop_over" | "last_stop";
  isShuttle?: boolean;
  stopPlaceRef?: string
  // Stations the train calls at without the passenger changing trains
  // (timedLeg.intermediates). Only set on a leg's boarding stop.
  calls?: TrainCallType[];
};

export type TrainCallType = {
  stopPlaceRef: string;
  name: string;
  time: string;
};


export interface Origin {
  stopPlaceRef: string
  objectType: string
}

export interface Destination {
  stopPlaceRef: string
  objectType: string
}

export type Leg = {
  timedLeg?: {
    start: {
      stopPlaceName: string;
      serviceDeparture: {
        timetabledTime: string; // ISO string
      };
    };
    end: {
      stopPlaceName: string;
      serviceArrival: {
        timetabledTime: string; // ISO string
      };
    };
    service: {
      vehicleNumbers: string[];
    };
  };
}

export type ITrainTypeClass = "standardValue" | "standardPro" | "premium" | "shuttle" | "standardDuo" | "standardQuad" | "premiumDuo" | "premiumQuad";
export type ProductCode = ITrainTypeClass | (string & {});
export type IServiceClass = "STANDARD" | "PREMIUM";

/** offerPassengerRefs for the price class the user actually selected (INDIVIDUAL offerMode). */
export function getPriceCategoryOfferPassengerRefs(
    priceInfo: PriceInfo | undefined,
    selectedPriceClass: ITrainTypeClass | null | undefined
): OfferPassengerRef[] {
    if (!priceInfo || !selectedPriceClass) return [];
    const key = selectedPriceClass as keyof PriceInfo;
    const cat = priceInfo[key];
    if (cat && typeof cat === "object" && "offerPassengerRefs" in cat) {
        return (cat as PriceCategory).offerPassengerRefs ?? [];
    }
    return [];
}

/** Find a dynamic product by its productCode across both standard and premium arrays. */
export function findDynamicProduct(priceInfo: PriceInfo | undefined, productCode: string): DynamicPriceCategory | undefined {
    if (!priceInfo) return undefined;
    return priceInfo.standardProducts?.find(p => p.productCode === productCode)
        ?? priceInfo.premiumProducts?.find(p => p.productCode === productCode);
}

/** Check whether a productCode (or legacy key) belongs to the Standard service class. */
export function isStandardProductCode(priceInfo: PriceInfo | undefined, code: string): boolean {
    const legacyStandard = ["standardValue", "standardPro", "standardDuo", "standardQuad"];
    if (legacyStandard.includes(code)) return true;
    return !!priceInfo?.standardProducts?.some(p => p.productCode === code);
}

/** Check whether a productCode (or legacy key) belongs to the Premium service class. */
export function isPremiumProductCode(priceInfo: PriceInfo | undefined, code: string): boolean {
    const legacyPremium = ["premium", "premiumDuo", "premiumQuad"];
    if (legacyPremium.includes(code)) return true;
    return !!priceInfo?.premiumProducts?.some(p => p.productCode === code);
}

export type shuttleTrain = {
  id: number | string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  isSoldOut: boolean;
  stops: string[];
  trainNumbers: string;
  priceInfo: string | number;
  type: string;
  selectedPriceClass?: ITrainTypeClass | null;
  trainNo?: string;
  transfers?: number;
  fromName?: string;
  toName?: string;
  direction?: string;
  origin?: Origin;
  destination?: Destination;
  startTime?: string;
  endTime?: string;
  legs?: Leg[];
  trainType?: string;
  descriptionText?: string;
};

export type TrainType = {
  id: number | string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  durationInMinutes: number;
  isSoldOut: boolean;
  stops: StopType[];
  trainNumbers: string;
  priceInfo: PriceInfo;
  type: string;
  selectedPriceClass?: ITrainTypeClass | null;
  trainNo?: string;
  transfers?: number;
  originShuttle?: shuttleTrain;
  destinationShuttle?: shuttleTrain;
  originInboundShuttle?: shuttleTrain;
  destinationInboundShuttle?: shuttleTrain;
  fromName?: string;
  toName?: string;
  trainType?: "train" | "shuttle" | "shuttleTrain";
  shortDate?: string,
  legs?: ILeg[];
  bookingCode?: string;
  descriptionText?: string;
  standardOffers?: IOffer[];
  standardProOffers?: IOffer[];
  premiumOffers?: IOffer[];
  shuttleOffers?: IOffer[];
  standardDuoOffers?: IOffer[];
  standardQuadOffers?: IOffer[];
  premiumDuoOffers?: IOffer[];
  premiumQuadOffers?: IOffer[];
  observedStartTime?: string;
  observedEndTime?: string;
};

export type IBookedTrainType = {
  id: number | string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  durationInMinutes: number;
  stops: StopType[];
  trainNumbers: string;
  type: TrainTypeEnum.outbound | TrainTypeEnum.inbound;
  priceClass?: ITrainTypeClass | null;
  trainNo?: string;
  transfers?: number;
  originShuttle?: shuttleTrain;
  destinationShuttle?: shuttleTrain;
  originInboundShuttle?: shuttleTrain;
  destinationInboundShuttle?: shuttleTrain;
  from: string;
  to: string;
  shortDate?: string,
  legs?: ILeg[];
  bookingCode?: string;
  descriptionText?: string;
  bookedOffers: IBookedOffer[]
  legTrains: ILegTrain[];
}
